import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { fetchNotificationsData } from "../services/notificationService";

// Shared notification state: the Header badge and the /notifications page
// read the SAME data, so one API request serves both and the badge count
// always matches the page. No polling — data loads on mount / auth change
// and refreshes only on explicit events (manual refresh, cross-tab storage
// updates of the local Next Service Mileage mirror, or the
// `tasmeh:notifications-changed` window event fired after local mutations).

const NotificationContext = createContext(null);

const LOCAL_MIRROR_KEY = "tasmeh.nextServiceMileage.v1";
export const NOTIFICATIONS_CHANGED_EVENT = "tasmeh:notifications-changed";

export function notifyNotificationsChanged() {
  try {
    window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED_EVENT));
  } catch {
    // Non-blocking: badge simply refreshes on next mount.
  }
}

export function NotificationProvider({ children }) {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [notifications, setNotifications] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Explicit flag (separate from the list itself): only an AUTHENTICATED
  // fetch marks data loaded. The logged-out empty list must NOT count as
  // loaded, otherwise logging in later in the same session would skip the
  // initial fetch and the badge would stay empty.
  const [loaded, setLoaded] = useState(false);
  const refreshSeq = useRef(0);

  const refresh = useCallback(async () => {
    const seq = ++refreshSeq.current;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchNotificationsData();
      // Ignore stale responses when a newer refresh started meanwhile.
      if (refreshSeq.current === seq) {
        setNotifications(Array.isArray(data) ? data : []);
        setLoaded(true);
      }
      return data;
    } catch (err) {
      if (refreshSeq.current === seq) {
        setError(err);
        setNotifications([]);
        setLoaded(true);
      }
      return [];
    } finally {
      if (refreshSeq.current === seq) {
        setLoading(false);
      }
    }
  }, []);

  // Initial load + auth transitions. Waits for auth initialization first
  // (AuthProvider restores the session asynchronously); logged-out users
  // get an empty list (badge hidden) without hitting the API (avoids 401
  // redirects) and stay NOT loaded so a later login triggers a fetch.
  // Skips when data is already loaded/loading (e.g. the /notifications page
  // triggered the shared refresh first) — one request serves everyone.
  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (isAuthenticated) {
      if (!loaded && !loading) {
        refresh();
      }
    } else {
      refreshSeq.current += 1; // invalidate any in-flight authed fetch
      setNotifications([]);
      setLoaded(false);
      setLoading(false);
      setError(null);
    }
  }, [authLoading, isAuthenticated, loaded, loading, refresh]);

  // Keep the badge fresh when notification data changes elsewhere:
  // another tab mutating the local mirror, or an in-app mutation event.
  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }
    const onStorage = (event) => {
      if (event?.key === LOCAL_MIRROR_KEY) {
        refresh();
      }
    };
    const onChanged = () => refresh();
    window.addEventListener("storage", onStorage);
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
    };
  }, [isAuthenticated, refresh]);

  const list = notifications ?? [];

  const value = {
    notifications: list,
    // No unread/read concept exists on the backend — every due reminder
    // IS a notification, so the badge shows the total count.
    count: list.length,
    unreadCount: list.length,
    loading: !loaded || loading,
    loaded,
    error,
    refresh,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

// Returns the context value, or null when rendered outside a provider
// (e.g. isolated unit tests). Never throws.
export function useNotifications() {
  return useContext(NotificationContext);
}

// Header-friendly selector. Outside a provider it falls back to a direct
// (de-duplicated) fetch so the badge still shows REAL data instead of a
// fake/hardcoded count. Skips the request when logged out (no auth token)
// so logged-out renders stay network-silent.
export function useNotificationCount() {
  const ctx = useContext(NotificationContext);
  const [fallbackList, setFallbackList] = useState([]);
  const [fallbackLoading, setFallbackLoading] = useState(false);

  useEffect(() => {
    if (ctx !== null) {
      return undefined;
    }
    let cancelled = false;
    try {
      if (!localStorage.getItem("authToken")) {
        return undefined;
      }
    } catch {
      return undefined;
    }
    setFallbackLoading(true);
    fetchNotificationsData()
      .then((data) => {
        if (!cancelled) {
          setFallbackList(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFallbackList([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setFallbackLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ctx]);

  if (ctx !== null) {
    return {
      count: ctx.count,
      unreadCount: ctx.unreadCount,
      loading: ctx.loading,
      refresh: ctx.refresh,
    };
  }
  return {
    count: fallbackList.length,
    unreadCount: fallbackList.length,
    loading: fallbackLoading,
    refresh: () => fetchNotificationsData(),
  };
}

export default NotificationContext;
